import React, {useCallback, useMemo, useState} from "react";
import {EmployeeDetails, EmployeeTimeEntry} from "@/data/entities/employee";
import {DataTable, DataTableColumnProps, DataTableUrl, OnErrorFn, SectionHeading, useNotifications} from "peggirkit";
import {paginationSummaryFormatter} from "@/utils/PaginationUtils";
import {FormattedMessage, useIntl} from "react-intl";
import {notifyDanger} from "@/utils/NotifyUtils";
import {
    getClockedHoursDataTableColumns
} from "@widgets/employees/editor/timeEntries/columns/ClockedHoursDataTableColumns";
import {eatalianoAxios} from "@/data/api/http/axios";

export type EmployeeTimeEntryDataTable = EmployeeTimeEntry & {
    summary: string,
    options: string,
};

const itemsPerPage = 20;

type Props = {
    employee: EmployeeDetails,
};

const ClockedHoursOverview = ({employee}: Props) => {
    const intl = useIntl();
    const {notify} = useNotifications();
    const [lastUpdated, setLastUpdated] = useState(new Date());
    const dataUrl: DataTableUrl = useCallback(() => {
        const dataUrl = new URL(`${process.env.BACKEND_API_BASE_URL}/employees/${employee.id}/time-entries`);
        return dataUrl.toString();
    }, [employee.id]);
    const columns: DataTableColumnProps<EmployeeTimeEntryDataTable>[] = useMemo(
        () => getClockedHoursDataTableColumns(intl, setLastUpdated),
        [intl, setLastUpdated]);
    const onError: OnErrorFn = useCallback(
        () => notifyDanger(intl.formatMessage({id: "operationError"}), notify),
        [intl, notify]);

    return (
        <div className={"w-full flex flex-col gap-6"}>
            <SectionHeading
                title={<FormattedMessage id={"clockedInTime"}/>}
                description={<FormattedMessage id={"clockedInTimeDescription"}/>}
            />

            <DataTable<EmployeeTimeEntryDataTable>
                key={lastUpdated.toISOString()}
                label={intl.formatMessage({id: "clockedInTime"})}
                url={dataUrl}
                axios={eatalianoAxios}
                paginationOptions={{
                    pageSize: itemsPerPage,
                    pageNumber: 0,
                    scrollToTopOnPageChange: true,
                    currentPageParameter: "page",
                }}
                columnSortingOptions={{
                    sortParameterName: "sort",
                }}
                paginationControl={{
                    navType: {type: "prevNext"},
                    paginationSummaryFormatter: paginationSummaryFormatter(intl),
                    breakpoints: {
                        sm: {navType: {type: "buttons"}},
                    },
                }}
                loadingConfig={{
                    skeletonRepeat: itemsPerPage,
                }}
                rowConfig={{
                    hoverEffect: true,
                }}
                columns={columns}
                size={"compact"}
                onError={onError}
                breakpoints={{md: {size: "normal"}}}
            />
        </div>
    );
};

export default ClockedHoursOverview;